import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  effect,
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { SupportedDevices } from '../../interfaces/supported-devices';
import { THEME_SIGNAL } from '../../interfaces/theme';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { SUPPORTED_DEVICES_TOKEN } from '../../tokens/server-tokens';

const SUPPORTED_DEVICES_TRANSFER_KEY = makeStateKey<SupportedDevices[]>(
  'SUPPORTED_DEVICES_TRANSFER_KEY'
);

@Injectable({
  providedIn: 'root',
})
export class SupportedDevicesService {
  public supportedDevices = signal<SupportedDevices[]>([]);

  protected readonly supportedDevicesManager = inject(SUPPORTED_DEVICES_TOKEN, { optional: true });

  protected cacheService = inject(CacheService);

  protected transferState = inject(TransferState);

  protected platform = inject(PLATFORM_ID);

  protected apiConfig = inject(ApiConfigService);

  protected http = inject(HttpClient);

  protected subscriberService = inject(SubscriberService);

  private readonly theme = inject(THEME_SIGNAL);

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      this.supportedDevices.set(this.transferState.get(SUPPORTED_DEVICES_TRANSFER_KEY, []));
    }

    effect(() => {
      this.subscriberService.subscriberLanguage();

      if (isPlatformServer(this.platform)) {
        return;
      }

      this.fetchFromApi().subscribe({
        next: (value) => {
          this.supportedDevices.set(value);
        },
      });
    });
  }

  public fetchFromApi(): Observable<SupportedDevices[]> {
    const host = this.apiConfig.getHost();
    const cachedContent = this.cacheService.cachedContent();

    return this.http.get<SupportedDevices[]>(
      this.apiConfig.hostApiUrl('supported-devices-translations'),
      {
        params: {
          host,
          s3Bucket: cachedContent?.cache.config.webAppConfig.s3BucketAssets || '',
          customerId: cachedContent?.cache.customerId || '',
          language: this.subscriberService.subscriberLanguage()?.code || '',
        },
      }
    );
  }

  public directFetchInServer(): Observable<SupportedDevices[]> {
    if (!this.supportedDevicesManager) {
      throw new Error('Supported Device Manager is not initialized');
    }

    const cachedContent = this.cacheService.cachedContent();

    if (!cachedContent) {
      throw new Error('Cached Content is not initialized');
    }

    return from(
      this.supportedDevicesManager.fetchSupportedDevicesContent(
        cachedContent.cache.config.webAppConfig.s3BucketAssets,
        cachedContent.cache.customerId,
        this.cacheService.getHost(),
        cachedContent.cache.config.languages.default,
        this.theme()
      )
    );
  }

  public async initInServer(): Promise<SupportedDevices[]> {
    const supportedDevices = await firstValueFrom(this.directFetchInServer());

    this.supportedDevices.set(supportedDevices);
    this.transferState.set(SUPPORTED_DEVICES_TRANSFER_KEY, supportedDevices);

    return supportedDevices;
  }
}
